/**
 * Signed-in landing hint (AIR-877).
 *
 * The real session cookie (`__session`, lib/firebase/auth-config.ts) is
 * httpOnly, so the static marketing page Firebase Hosting serves at the
 * portal root (statics beat the `**` → Cloud Run rewrite) cannot see it.
 * This module maintains a tiny NON-httpOnly companion cookie whose only job
 * is to tell that static page "a session likely exists on this host — send
 * the visitor into the app". public/index.html reads it in a synchronous
 * inline <head> script and location.replace()s to /home before paint.
 *
 * Client-set (document.cookie) on purpose: Firebase Hosting only reliably
 * passes the `__session` cookie between the CDN and Cloud Run, so a server
 * Set-Cookie under any other name may never reach the browser on
 * hosting-fronted domains. document.cookie never leaves the browser, so the
 * CDN cannot interfere — and the server never needs to read the hint.
 *
 * The cookie is host-only (no Domain attribute), so it can only exist on the
 * host where the sign-in actually happened — the portal host. Apex/marketing
 * visitors and crawlers never carry it, which is what keeps the static page
 * byte-identical for signed-out traffic with no hostname allowlist to
 * maintain per environment.
 *
 * Lifecycle (all in AuthProvider): set right after the verified /api/login
 * cookie sync, re-set on any app page load with a live server session
 * (seeds sessions that predate this cookie and slides expiry like
 * `__session` does on token refresh), cleared on sign-out and on any app
 * page load that is definitively signed out. The hint is best-effort and
 * NEVER trusted for auth — drift in either direction is benign:
 * - hint without session: "/" bounces into the app and the auth middleware
 *   redirects to /login (no loop); the next signed-out app page load clears
 *   the stale hint.
 * - session without hint: "/" shows marketing (pre-AIR-877 behavior); the
 *   next app page load re-seeds the hint.
 */

export const SESSION_HINT_COOKIE = "aud_session_hint";

/**
 * Matches `__session`'s maxAge (auth-config.ts cookieSerializeOptions) so
 * both expire together. Not imported from there: auth-config reads
 * server-only env vars and this module ships in the client bundle. The
 * equality is pinned by __tests__/session-hint.test.ts.
 */
export const SESSION_HINT_MAX_AGE_SECONDS = 12 * 60 * 60 * 24;

export function sessionHintSetCookie(secure: boolean): string {
  return `${SESSION_HINT_COOKIE}=1; path=/; max-age=${SESSION_HINT_MAX_AGE_SECONDS}; samesite=lax${secure ? "; secure" : ""}`;
}

export function sessionHintClearCookie(secure: boolean): string {
  return `${SESSION_HINT_COOKIE}=; path=/; max-age=0; samesite=lax${secure ? "; secure" : ""}`;
}

function isSecureContext(): boolean {
  return window.location.protocol === "https:";
}

/** Publish the hint on this host. Browser-only; no-op during SSR. */
export function setSessionHintCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = sessionHintSetCookie(isSecureContext());
}

/** Drop the hint so "/" serves the marketing page again. No-op during SSR. */
export function clearSessionHintCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = sessionHintClearCookie(isSecureContext());
}
